import { EMPTY, interval, merge, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  endWith,
  filter,
  map,
  mergeWith,
  startWith,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getSavedConcurrencyObject,
  setSavedConcurrencyObject,
} from '../../store/savedConcurrencyObject';
import {
  CONCURRENCY_UNLOCK,
  MEDIA_ERROR,
  MEDIA_PAUSE,
  MEDIA_SEEKED,
  MEDIA_TIMEUPDATE,
  PLAYER_SHUTDOWN,
  PLUGIN_ERROR,
} from '../../core/events';
import type { ClientPlatform, Media, UnifiedPlayer } from '../../types';
import { trackPluginCall } from '../../util/overseer/overseer';

type UnlockTypes = { media: Media; currentTime?: number };

type UpdateTypes = UnlockTypes & { event: string };

export default (clientPlatform: ClientPlatform) => {
  const UNLOCK_EVENTS = ['SHUTDOWN', 'STOP', 'UNLOCK'];
  const subscriptionToCleanOnDestroy: Subscription[] = [];

  function unsubscribeAllSubscriptions() {
    subscriptionToCleanOnDestroy.forEach(s => s.unsubscribe());
  }
  const plugin = function progressConcurrencyPlugin(_: any, player: UnifiedPlayer) {
    const pause$ = player.observe(MEDIA_PAUSE).pipe(map(() => 'PAUSE'));
    const seeked$ = player.observe(MEDIA_SEEKED).pipe(map(() => 'SCRUB'));
    const shutdown$ = player.observe(PLAYER_SHUTDOWN).pipe(map(() => 'SHUTDOWN'));
    const media$ = player.observe<Media>(data => data.media);
    const manualUnlock$ = player.observe(CONCURRENCY_UNLOCK).pipe(map(() => 'UNLOCK'));

    const currentTime$ = player
      .observe(MEDIA_TIMEUPDATE)
      .pipe(map(e => e.currentTime || e.currentMediaTime || player.currentTime));

    const destroy$ = player.observe(MEDIA_ERROR).pipe(
      mergeWith(player.observe().pipe(endWith('destroy'))),
      map(() => 'STOP')
    );

    const defaultUpdateInterval = 30;
    const parseUpdateInterval = (updateInterval: any) =>
      parseInt(updateInterval === 0 ? defaultUpdateInterval : updateInterval, 10) * 1000;

    const progressActions$ = merge(pause$, seeked$).pipe(
      filter(() => player.duration !== Infinity)
    );

    // #region concurrency reporting
    const update = ({ event, media, currentTime }: UpdateTypes) => {
      const { user, concurrency, profile } = media;

      const isConcurrencyUpdate = event === 'updateConcurrency';

      const body = {
        ...concurrency!.body,
        ...(isConcurrencyUpdate
          ? { userAction: null, concurrencyAction: 'UPDATE' }
          : { userAction: event, concurrencyAction: 'UPDATE' }),
        progress: media.metadata.isLive ? null : currentTime,
      };

      fetch(concurrency!.url!, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json',
          'Client-Platform-Class': clientPlatform.class,
          'Client-Platform': clientPlatform.name,
          'Client-Platform-Audience': clientPlatform.audience,
          'Client-Platform-AppVersion': clientPlatform.version!,
          'Client-Platform-User-Profile': `${profile?.profileToken ?? ''}`,
        },
      })
        .then(() => trackPluginCall('Concurrency', 'POST_UPDATE'))
        .catch(error => {
          trackPluginCall('Concurrency', 'PLUGIN_ERROR', error);
          player.emit(PLUGIN_ERROR, { name: 'progressConcurrency', error });
        });
    };

    const unlock = ({ media, currentTime }: UnlockTypes) => {
      if (!getSavedConcurrencyObject()) return;
      try {
        const { user, concurrency, profile } = media;

        const body = {
          ...concurrency!.body,
          concurrencyAction: 'UNLOCK',
          progress: media.metadata.isLive ? null : currentTime,
        };

        setSavedConcurrencyObject(undefined);
        fetch(concurrency!.url!, {
          method: 'POST',
          keepalive: true,
          body: JSON.stringify(body),
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
            'Content-Type': 'application/json',
            'Client-Platform-Class': clientPlatform.class,
            'Client-Platform': clientPlatform.name,
            'Client-Platform-Audience': clientPlatform.audience,
            'Client-Platform-AppVersion': clientPlatform.version!,
            'Client-Platform-User-Profile': `${profile?.profileToken ?? ''}`,
          },
        })
          .then(() => trackPluginCall('Concurrency', 'POST_UNLOCK'))
          .catch(error => {
            trackPluginCall('Concurrency', 'PLUGIN_ERROR', error);
            player.emit(PLUGIN_ERROR, { name: 'progressConcurrency', error });
          });
      } catch (error) {
        trackPluginCall('Concurrency', 'PLUGIN_ERROR', error);

        player.emit(PLUGIN_ERROR, { name: 'progressConcurrency', error });
      }
    };
    // #endregion

    const subscription = media$
      .pipe(
        filter(media => media?.concurrency !== undefined),
        catchError(() => EMPTY),
        switchMap(media => {
          return merge(
            interval(parseUpdateInterval(media?.concurrency?.updateInterval)).pipe(
              map(() => 'updateConcurrency'),
              takeUntil(destroy$)
            ),
            progressActions$,
            destroy$,
            shutdown$,
            manualUnlock$
          ).pipe(map(event => ({ event, media })));
        }),
        distinctUntilChanged((p, c) => {
          return UNLOCK_EVENTS.includes(p.event) && UNLOCK_EVENTS.includes(c.event);
        }),
        withLatestFrom(currentTime$.pipe(startWith(undefined)))
      )
      .subscribe(
        ([{ media, event }, currentTime]) => {
          trackPluginCall('Concurrency', 'EVENT', { media, event, currentTime });

          if (UNLOCK_EVENTS.includes(event)) {
            unlock({ media, currentTime });
          } else {
            update({ event, media, currentTime });
          }
        },
        error => {
          trackPluginCall('Concurrency', 'PLUGIN_ERROR', error);
          player.emit(PLUGIN_ERROR, { name: 'progressConcurrency', error });
        }
      );

    subscriptionToCleanOnDestroy.push(subscription);
  };
  plugin.destroy = unsubscribeAllSubscriptions;
  return plugin;
};
